import { AlertTriangle, Flag, MessageSquare, UserRound, Briefcase, IdCard, Check, X } from "lucide-react";
import stickyNoteIcon from "assets/icons/sticky-note-01.svg";
import avisIcon from "assets/icons/avis.svg";
import { V2 } from "layout/Dashboard/Header/headerV2Tokens";

const CRITICAL = "#B42318";
const SUCCESS = "#027A48";

/**
 * Shared between the notification bell (Notification.jsx) and the toast
 * (NotificationContext.jsx) so both render the same icon/color for a given
 * notification type — originally duplicated and started drifting.
 *
 * Three content types get an avatar-style icon instead of a generic badge
 * (there's no actor to show, unlike reports/comments, so the icon stands
 * in for one): a regular news article, the same article flagged critic
 * ("alert" — its own icon/color regardless of category, since the point
 * is urgency), and an expert opinion.
 */
export function getTypeAvatarProps(notification) {
  const related = notification.related_object || {};
  if (notification.notification_type === "NEW_NEWS") {
    if (related.is_critic) {
      return { kind: "alert", Icon: AlertTriangle, color: CRITICAL };
    }
    // Same icon the sidebar uses for Veille (menu-items/veille.jsx), not a
    // generic lucide glyph — fixed brand blue.
    return { kind: "actualite", iconSrc: stickyNoteIcon, color: V2.accent };
  }
  if (notification.notification_type === "NEW_EXPERT_OPINION") {
    // Same icon the sidebar uses for Avis (menu-items/avis.jsx).
    return { kind: "avis", iconSrc: avisIcon, color: V2.accent };
  }
  // Picture moderation outcomes — generic avatar glyph (not the user's own
  // photo: that was removed, the point is "what happened" not "what you
  // look like"), same icon + corner status-dot pattern as report/comment.
  if (notification.notification_type === "PICTURE_VALIDATED") {
    return { kind: "picture_validated", Icon: UserRound, color: V2.accent, statusColor: SUCCESS, StatusIcon: Check };
  }
  if (notification.notification_type === "PICTURE_REJECTED") {
    return { kind: "picture_rejected", Icon: UserRound, color: V2.accent, statusColor: CRITICAL, StatusIcon: X };
  }
  // Report/comment outcomes: the main icon says WHAT this is (a report, a
  // comment) in the same neutral blue as news/avis, and a small colored
  // dot overlaid on its corner — like an online-status indicator, with its
  // own tiny check/x glyph — says WHAT HAPPENED (green check = approved/
  // resolved, red x = rejected/dismissed/deleted), instead of the status
  // taking over the whole icon.
  if (notification.notification_type === "REPORT_RESOLVED") {
    return { kind: "report_resolved", Icon: Flag, color: V2.accent, statusColor: SUCCESS, StatusIcon: Check, showMessage: true };
  }
  if (notification.notification_type === "REPORT_DISMISSED") {
    return { kind: "report_dismissed", Icon: Flag, color: V2.accent, statusColor: CRITICAL, StatusIcon: X, showMessage: true };
  }
  if (notification.notification_type === "COMMENT_VALIDATED") {
    return { kind: "comment_validated", Icon: MessageSquare, color: V2.accent, statusColor: SUCCESS, StatusIcon: Check };
  }
  if (notification.notification_type === "COMMENT_DELETED") {
    return { kind: "comment_deleted", Icon: MessageSquare, color: V2.accent, statusColor: CRITICAL, StatusIcon: X };
  }
  // Profession change request outcomes — same icon + corner status-dot
  // pattern, message shown (carries the target profession / admin's
  // rejection note, unlike picture validation which has nothing to add).
  if (notification.notification_type === "PROFESSION_CHANGE_APPROVED") {
    return { kind: "profession_change_approved", Icon: Briefcase, color: V2.accent, statusColor: SUCCESS, StatusIcon: Check, showMessage: true };
  }
  if (notification.notification_type === "PROFESSION_CHANGE_REJECTED") {
    return { kind: "profession_change_rejected", Icon: Briefcase, color: V2.accent, statusColor: CRITICAL, StatusIcon: X, showMessage: true };
  }
  // INP verification outcomes — same IdCard icon the verification page
  // itself uses. INP_VERIFICATION also covers the admin-facing "new
  // request" notification (no outcome yet), which gets the plain icon
  // with no status dot — only approved/rejected get one, detected from
  // the title text since there's no separate notification_type for them.
  if (notification.notification_type === "INP_VERIFICATION") {
    if (notification.title?.includes("approuvée")) {
      return { kind: "inp_approved", Icon: IdCard, color: V2.accent, statusColor: SUCCESS, StatusIcon: Check, showMessage: true };
    }
    if (notification.title?.includes("rejetée")) {
      return { kind: "inp_rejected", Icon: IdCard, color: V2.accent, statusColor: CRITICAL, StatusIcon: X, showMessage: true };
    }
    return { kind: "inp_request", Icon: IdCard, color: V2.accent, showMessage: true };
  }
  return null;
}
